import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type NodeStatus =
  | "complete"
  | "running"
  | "paused"
  | "queued"
  | "error"
  /** #854: a Fase A terminou e a decisão de escopo está em aberto. */
  | "awaiting";

interface PipelineNode {
  id: string;
  shortName: string;
  status: NodeStatus;
  meta?: string;
  tag?: string;
  tabsCount?: number;
}

interface PipelineProgressProps {
  nodes: PipelineNode[];
  activeNode?: string;
  onNodeClick?: (id: string) => void;
  heading?: string;
  interactive?: boolean;
}

export function PipelineProgress({ nodes, activeNode, onNodeClick, heading, interactive = true }: PipelineProgressProps) {
  const { t } = useTranslation();
  const errorIdx = nodes.findIndex((n) => n.status === "error");
  const runningIdx = nodes.findIndex((n) => n.status === "running");
  const pausedIdx = nodes.findIndex(
    (n) => n.status === "paused" || n.status === "awaiting",
  );
  const completedIdx = nodes.map((n) => n.status).lastIndexOf("complete");
  // Progress bar para até o ponto atual — pause também pinta verde até
  // o nó anterior, como uma run em andamento.
  const activeIdx =
    errorIdx >= 0
      ? errorIdx
      : runningIdx >= 0
        ? runningIdx
        : pausedIdx >= 0
          ? pausedIdx
          : completedIdx + 1;
  const progressPct = nodes.length <= 1 ? 0 : Math.max(0, Math.min(1, activeIdx / (nodes.length - 1))) * 100;
  const progressColor = errorIdx >= 0 ? "#d93636" : "#27a155";

  const doneCount = nodes.filter((n) => n.status === "complete").length;
  const pausedCount = nodes.filter((n) => n.status === "paused").length;
  const awaitingCount = nodes.filter((n) => n.status === "awaiting").length;
  const pendingCount = nodes.filter((n) => n.status === "queued").length;
  const errorCount = nodes.filter((n) => n.status === "error").length;
  const viewingIdx = activeNode ? nodes.findIndex((n) => n.id === activeNode) : -1;
  const viewingNumber = viewingIdx >= 0 ? viewingIdx + 1 : null;
  const NodeElement = interactive ? "button" : "div";

  return (
    <section className="bg-white border-y border-[#e8ebf0] px-8 pt-6 pb-7 relative">
      <div className="font-mono text-[10px] text-[#ffc83d] tracking-[0.22em] uppercase font-semibold mb-2.5 flex justify-between items-center">
        <span>{heading ?? t("The Pipeline · {{count}} Nodes · click a node to inspect", { count: nodes.length })}</span>
        <span className="text-[#7b8799] font-medium">
          {viewingNumber != null && <>{t("Node {{num}} viewing", { num: viewingNumber })} · </>}
          {t("{{count}} done", { count: doneCount })} ·{" "}
          {pausedCount > 0 && (
            <>
              <span className="text-[#b57a00] font-semibold">
                {t("{{count}} paused", { count: pausedCount })}
              </span>
              {" · "}
            </>
          )}
          {awaitingCount > 0 && (
            <>
              <span className="text-[#b57a00] font-semibold">
                {t("{{count}} awaiting your decision", { count: awaitingCount })}
              </span>
              {" · "}
            </>
          )}
          {t("{{count}} pending", { count: pendingCount })}
          {errorCount > 0 && (
            <>
              {" · "}
              <span className="text-[#d93636] font-semibold">
                {t("{{count}} errored", { count: errorCount })}
              </span>
            </>
          )}
        </span>
      </div>
      <div
        role={interactive ? undefined : "list"}
        aria-label={interactive ? undefined : heading}
        className="relative grid items-start"
        style={{
          gridTemplateColumns: `repeat(${nodes.length}, 1fr)`,
        }}
      >
        <div
          aria-hidden="true"
          className="absolute left-5 right-5 top-6 h-[3px] rounded"
          style={{
            background: `linear-gradient(90deg, ${progressColor} 0%, ${progressColor} ${progressPct}%, #e8ebf0 ${progressPct}%, #e8ebf0 100%)`,
          }}
        />
        {nodes.map((node, i) => {
          const isRunning = node.status === "running";
          // #854: aguardando decisão usa o mesmo âmbar do pausado, porque as
          // duas coisas querem a mesma ação (alguém precisa olhar). O que muda
          // é o rótulo: a run não está pausada, ela terminou.
          const isPaused =
            node.status === "paused" || node.status === "awaiting";
          const isViewing = !!activeNode && node.id === activeNode;
          const isDone = node.status === "complete";
          const isError = node.status === "error";
          const isQueued = node.status === "queued" && !isRunning && !isPaused;
          const num = String(i + 1).padStart(2, "0");
          const metaText =
            node.tabsCount != null
              ? t("{{meta}} · {{count}} tabs", { meta: node.meta ?? "—", count: node.tabsCount })
              : node.meta;

          return (
            <NodeElement
              key={node.id}
              type={interactive ? "button" : undefined}
              role={interactive ? undefined : "listitem"}
              aria-label={interactive ? undefined : `${node.shortName}: ${t(isDone ? "Completed" : isError ? "Errored" : isRunning ? "Now running" : isPaused ? "Awaiting input" : "Pending")}`}
              onClick={interactive ? () => onNodeClick?.(node.id) : undefined}
              aria-current={isViewing ? "step" : undefined}
              className={cn("relative flex flex-col items-center gap-2.5 z-[2] px-1.5 group", interactive && "cursor-pointer focus:outline-none")}
            >
              <span
                className={cn(
                  "relative w-12 h-12 rounded-full flex items-center justify-center font-mono text-xs font-bold tracking-[0.02em] transition-all z-[2] border-[3px] bg-white",
                  isDone && "text-white",
                  isError && "text-white",
                  (isRunning || isPaused) && "text-[#b57a00]",
                  isQueued && "text-[#b2bccb]",
                )}
                style={
                  isDone
                    ? { background: "#27a155", borderColor: "#27a155", color: "#fff" }
                    : isError
                      ? {
                          background: "#d93636",
                          borderColor: "#d93636",
                          color: "#fff",
                          boxShadow: "0 0 0 4px rgba(217,54,54,0.18), 0 0 16px rgba(217,54,54,0.35)",
                        }
                      : isRunning
                        ? {
                            borderColor: "#ffc83d",
                            boxShadow: "0 0 0 4px rgba(255,200,61,0.18), 0 0 16px rgba(255,200,61,0.4)",
                          }
                        : isPaused
                          ? {
                              // Mesmo amarelo do running, mas sem pulse — sinaliza
                              // "esperando input humano", não "trabalhando agora".
                              borderColor: "#ffc83d",
                              boxShadow:
                                "0 0 0 4px rgba(255,200,61,0.18), 0 0 16px rgba(255,200,61,0.4)",
                            }
                          : { borderColor: "#e0e6ed" }
                }
              >
                {isDone ? (
                  <Check className="w-[18px] h-[18px]" strokeWidth={2.25} />
                ) : isError ? (
                  <X className="w-[18px] h-[18px]" strokeWidth={2.5} />
                ) : (
                  num
                )}
                {isRunning && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-[-8px] rounded-full mg-pulse-scale motion-reduce:hidden"
                    style={{ border: "1.5px solid #ffc83d", opacity: 0.5 }}
                  />
                )}
                {isViewing && !isRunning && !isError && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-[-6px] rounded-full"
                    style={{ outline: "2px solid #08254c", outlineOffset: "2px" }}
                  />
                )}
              </span>
              <span
                className={cn(
                  "font-mono text-[9px] tracking-[0.12em] uppercase",
                  isError
                    ? "text-[#d93636] font-bold"
                    : isRunning || isPaused
                      ? "text-[#b57a00] font-bold"
                      : isViewing
                        ? "text-[#08254c] font-bold"
                        : "text-[#b2bccb]",
                )}
              >
                {node.tag ??
                  (isError
                    ? t("Errored")
                    : isRunning
                      ? t("Now running")
                      : isPaused
                        ? t("Awaiting input")
                        : isDone
                          ? t("Node {{num}}", { num })
                          : t("Pending"))}
              </span>
              <span
                className={cn(
                  "font-brand font-bold text-[13px] tracking-[-0.005em]",
                  isError
                    ? "text-[#d93636]"
                    : isRunning || isPaused
                      ? "text-[#b57a00]"
                      : isViewing
                        ? "text-[#08254c] font-extrabold"
                        : isQueued
                          ? "text-[#7b8799] font-medium"
                          : "text-[#08254c]",
                )}
              >
                {node.shortName}
              </span>
              {metaText && (
                <span className="font-mono text-[10px] text-[#7b8799] tracking-[0.02em] text-center">{metaText}</span>
              )}
            </NodeElement>
          );
        })}
      </div>
    </section>
  );
}

export type { PipelineNode, NodeStatus };
